import {
  Component,
  ComponentInterface,
  Element,
  Event,
  EventEmitter,
  Listen,
  Prop,
  State,
  Watch,
  h,
} from '@stencil/core';
import { ActionTrackingEvent } from '@union-investment/tracking/src/events';
import {
  getExternalLinkAttributes,
  getInternalLinkAttributes,
  isExternalLink,
} from '../../../global/utility/tracking-helpers';
import { CmsLink } from '../../../types/global';

export interface UiHeaderDropdown {
  title: string;
  iconOption?: 'icon' | 'none';
  iconPath?: string;
  titleDisplayPosition?: 'title-in-dropdown' | 'title-next-to-icon';
  dropdownLinks: CmsLink[];
}

interface SearchDefinition {
  targetPageUrl: string;
  suggestionUrl: string;
  apiKey: string;
}

interface CmsLinksHeaderLocalizedText {
  languageSwitchLabel?: string;
  search?: string;
}

export interface UiHeaderServiceArea {
  singleLinks?: CmsLink[];
  languageLinks?: CmsLink[];
  dropdowns?: UiHeaderDropdown[];
  search?: SearchDefinition;
  icon?: string;
  languageSwitchIcon?: string;
  algoliaSearchTargetUrl?: string;
}

@Component({
  tag: 'ui-cms-header-service-area',
  styleUrl: 'ui-cms-header-service-area.scss',
})
export class UiCmsHeaderServiceArea implements ComponentInterface {
  @Element() el: HTMLUiCmsHeaderServiceAreaElement;

  LinksWrapper: HTMLUListElement;

  @Prop({ mutable: true }) body: UiHeaderServiceArea = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Prop() navigationWidth: number;

  @Prop() linksHeaderWidth: number;

  @State() searchOpen = false;

  @Watch('utilityBody')
  bodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @Event() linksNavigationReady: EventEmitter;

  @Prop({ mutable: true }) i18n: CmsLinksHeaderLocalizedText = { languageSwitchLabel: 'Sprachen', search: 'Suche' };

  @Prop({ attribute: 'i18n' }) utilityI18n: string;

  @Watch('utilityI18n')
  utilityI18nChangeHandler(newValue: string) {
    if (newValue) this.i18n = { ...this.i18n, ...JSON.parse(newValue) };
  }

  @Watch('navigationWidth')
  mainNavigationWidthUpdated(newValue: string) {
    this.navigationWidth = newValue ? parseInt(newValue, 10) : this.navigationWidth;
  }

  async componentWillLoad() {
    this.bodyChangeHandler(this.utilityBody);
    this.linksNavigationReady.emit();
    this.utilityI18nChangeHandler(this.utilityI18n);
  }

  componentDidLoad() {
    this.linksHeaderWidth = this.LinksWrapper?.offsetWidth;
  }

  renderLink(link: CmsLink) {
    return (
      <li key={link.title}>
        <sd-link
          href={link.src}
          target={link.target}
          onClick={() => {
            this.onClickSimpleLink.bind(this)(link);
          }}
        >
          {link.iconPath && <sd-icon library={window.ui.cmsState.iconLibrary} name={link.iconPath} />}
          <span aria-hidden={link.ariaLabel ? 'true' : 'false'}>{link.title}</span>
          <span class="sr-only">{link.ariaLabel}</span>
        </sd-link>
      </li>
    );
  }

  onClickSimpleLink(link: CmsLink) {
    const linkAttributes = isExternalLink(link.src) ? getExternalLinkAttributes(link) : getInternalLinkAttributes(link);

    const trackingEvent = {
      eventInfo: {
        eventName: 'Click Header Service Area Link',
        eventType: 'Action',
      },
      attributes: {
        elementType: 'Link',
        elementLabel: link.title,
        ...linkAttributes,
      },
      category: {
        area: 'Header',
      },
    };

    if (trackingEvent) {
      window.ui?.services?.tracking?.track(trackingEvent);
    }
  }

  @Listen('sd-show')
  onOpenDropdown(dropdown: UiHeaderDropdown) {
    const trackingEvent = {
      eventInfo: {
        eventName: 'Click Header Service Area Dropdown',
        eventType: 'Action',
      },
      attributes: {
        elementType: 'Button',
        elementLabel: dropdown.title,
      },
      category: {
        area: 'Header',
      },
    };

    if (trackingEvent) {
      window.ui?.services?.tracking?.track(trackingEvent);
    }
  }

  private renderDropdown(dropdownItem: UiHeaderDropdown) {
    const { iconPath, titleDisplayPosition, iconOption, title, dropdownLinks } = dropdownItem;
    return (
      <li class="dropdown">
        <sd-dropdown placement="bottom-end">
          <sd-navigation-item slot="trigger">
            {Boolean(iconPath) && iconOption === 'icon' && (
              <sd-icon library={window.ui.cmsState.iconLibrary} name={iconPath} label={title}></sd-icon>
            )}
            {titleDisplayPosition === 'title-next-to-icon' && <span>{title}</span>}
          </sd-navigation-item>
          <div class="dropdown__content">
            {titleDisplayPosition === 'title-in-dropdown' && (
              <h2 class="sd-headline sd-headline--size-base">{title}</h2>
            )}
            <ul>
              {dropdownLinks &&
                dropdownLinks.map((link) => (
                  <li>
                    <sd-link onClick={() => this.onClickSimpleLink(link)} target={link.target} href={link.src}>
                      {link.title}
                    </sd-link>
                  </li>
                ))}
            </ul>
          </div>
        </sd-dropdown>
      </li>
    );
  }

  renderLanguageSwitch() {
    const languageLinks = this.body?.languageLinks;

    if (!languageLinks || languageLinks.length === 0) {
      return null;
    }
    if (languageLinks.length === 1) {
      const link = languageLinks[0];
      return this.renderLink(link);
    }
    if (languageLinks.length > 1) {
      return this.renderDropdown({
        title: this.i18n?.languageSwitchLabel,
        iconPath: this.body?.languageSwitchIcon || 'globe',
        dropdownLinks: languageLinks,
        iconOption: 'icon',
        titleDisplayPosition: 'title-next-to-icon',
      });
    }
    return null;
  }

  toggleSearch(): void {
    this.searchOpen = !this.searchOpen;

    const searchOpenEvent: ActionTrackingEvent = {
      eventInfo: {
        eventType: 'Action',
        eventName: 'Search open',
      },
      category: {
        area: 'Header',
      },
      attributes: {
        elementLabel: 'Suchfeld_aktivieren',
        elementType: 'Button',
        target: window.location.href,
      },
    };

    const searchCloseEvent = {
      eventInfo: {
        eventType: 'Action',
        eventName: 'Search close',
      },
      category: {
        area: 'Header',
      },
      attributes: {
        elementLabel: 'Suchbegriff_löschen',
        elementType: 'Button',
      },
    };

    const trackingEvent = this.searchOpen ? searchOpenEvent : searchCloseEvent;
    window.ui?.services?.tracking?.track(trackingEvent);
  }

  onSearch(): void {
    const trackingEvent: ActionTrackingEvent = {
      eventInfo: {
        eventType: 'Action',
        eventName: 'Search Submit',
      },
      category: {
        area: 'Header',
      },
      attributes: {
        elementLabel: 'Header Service Area Search Field',
        elementType: 'Button',
        target: 'Startseite.Suche.Suchergebnisseite',
      },
    };

    if (trackingEvent) {
      window.ui?.services?.tracking?.track(trackingEvent);
    }
  }

  @Listen('click', { target: 'window' })
  closeSearchOnClickOutside(ev) {
    if (!this.searchOpen) {
      return;
    }

    // prevent TypeError: ev.path is undefined
    if (!ev.path) return;

    if (
      ev.path.some((el) => el === this.el) &&
      ev.path.some((el) => el.className === 'global-search' || el.className === 'global-search-trigger')
    ) {
      return;
    }

    this.searchOpen = false;
  }

  render() {
    const { search, algoliaSearchTargetUrl } = this.body;
    return (
      <ul
        class={{
          'service-nav global-search-wrapper': true,
          'search-active': this.searchOpen,
        }}
        ref={(el) => {
          this.LinksWrapper = el as HTMLUListElement;
        }}
      >
        {this.body.singleLinks?.map((link) => this.renderLink(link))}
        {this.body.dropdowns?.map((dropdownItem) => this.renderDropdown(dropdownItem))}
        {this.renderLanguageSwitch()}
        {(search || algoliaSearchTargetUrl) && [
          <li class="global-search-trigger" key="global-search-trigger">
            <button
              id="global-search-trigger"
              aria-label="Suche"
              class="suche open-search-form"
              onClick={() => this.toggleSearch()}
            ></button>
          </li>,
          <li key="global-search" class="global-search">
            {algoliaSearchTargetUrl ? (
              <ui-cms-algolia-search-field
                algoliaSearchTargetUrl={algoliaSearchTargetUrl}
                i18n={{ search: this.i18n.search }}
              ></ui-cms-algolia-search-field>
            ) : (
              <ui-cms-search-autosuggest
                searchUrl={search.targetPageUrl}
                suggestionUrl={search.suggestionUrl}
                apiKey={search.apiKey}
                inputInFocus={true}
                onSearch={this.onSearch.bind(this)}
              ></ui-cms-search-autosuggest>
            )}

            <a
              aria-label="Suche schließen"
              class="close-search close-search-form"
              onClick={() => this.toggleSearch()}
            ></a>
          </li>,
        ]}
      </ul>
    );
  }
}
